import { useStackNavigate } from '@/app/navigation/stack-links';
import { useTicketsStore } from '@/components/editor/TicketPlugin/TicketStore';
import { buildTicketUrl } from '@/lib/ticketUrls';
import {
  getEditorTicketClasses,
  getTicketLineThroughClasses,
  getTicketNumberClasses,
} from '@/lib/ticket-styles';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import React from 'react';
import { cn } from '@/lib/utils';

export function TicketComponent({
  ticketId,
  showTitle,
  nodeKey,
}: {
  ticketId: string;
  showTitle: boolean;
  nodeKey: string;
}) {
  const ticketStore = useTicketsStore();
  const [isSelected] = useLexicalNodeSelection(nodeKey);
  const isEditable = useLexicalEditable();

  // Function to format the ticket ID for display
  const formatTicketId = (id: string) => {
    // If it looks like a UUID (36 chars with dashes), show first 6 chars + ellipsis
    if (id.length === 36 && id.includes('-')) {
      return id.substring(0, 6) + '…';
    }
    return id;
  };
  const go = useStackNavigate();

  // Handle click to open ticket URL (disabled while editing rich text)
  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (isEditable) return;
    const ticket = ticketStore.getTicket(ticketId);
    if (ticket) {
      const url = buildTicketUrl(ticket);

      go({ to: url });
    }
  };

  if (ticketStore.isLoading) {
    return (
      <span
        className={cn(
          'bg-muted text-muted-foreground animate-pulse rounded px-1',
          !isEditable && 'cursor-pointer',
          isSelected && 'ring-2 ring-amber-500 ring-offset-1',
        )}
        onClick={handleClick}
      >
        <span className="font-mono">#{formatTicketId(ticketId)}</span>
      </span>
    );
  }

  // Look up ticket by ID, number, or recnum (all pre-indexed for O(1) performance)
  const ticket = ticketStore.getTicket(ticketId);

  const displayId = ticket?.number ?? formatTicketId(ticketId);
  const status = ticket?.status;

  return (
    <span
      className={cn(
        getEditorTicketClasses(status, isSelected),
        isEditable ? 'hover:no-underline' : 'cursor-pointer',
      )}
      title={ticket ? `${ticket.title} (${ticket.status})` : 'Ticket'}
      onClick={handleClick}
    >
      <span className={cn(getTicketNumberClasses(status))}>#{displayId}</span>
      {showTitle && ticket && (
        <span className={getTicketLineThroughClasses(status!)}>
          {' '}
          {ticket.title}
        </span>
      )}
    </span>
  );
}
